import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
	AiAnalysisApiResponse,
	ApiResponse,
	FinancialSummaryApiResponse,
	NewsletterControllerApiResponse,
	StockCompanyDetailsApiResponse,
	StockDataApiResponse,
	StockDetailsApiResponse,
	StockFilingsApiResponse,
	StockPriceChangeApiResponse,
} from "../models/api-response";
import { AppError } from "../models/app-error";
import { StockCompanyDetails } from "src/models/stock-types";

async function fetchApiData<T>(options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<T>> {
	try {
		const response: AxiosResponse = await axios(options.config);

		return {
			data: response.data.data,
			error: null,
		};
	} catch (error) {
		return handleError(error);
	}
}

async function fetchApiDataFlatJson<T>(options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<T>> {
	try {
		const response: AxiosResponse = await axios(options.config);

		return {
			data: response.data,
			error: null,
		};
	} catch (error) {
		return handleError(error);
	}
}

function handleError<T>(error: unknown): ApiResponse<T> {
	let message = "An error occurred while retrieving data.";

	if (axios.isAxiosError(error)) {
		const axiosError = error as AxiosError<AppError>;
		message = axiosError.message;

		if (axiosError.response) {
			message = axiosError.response.statusText || message;
			message = axiosError.response.data?.message || message;
		}
	} else if (error instanceof Error) {
		message = error.message;
	}

	return {
		data: null,
		error: { message },
	};
}

export const fetchStockDetails = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<StockDetailsApiResponse>> => {
	const data = fetchApiData<StockDetailsApiResponse>(options);
	return data;
};

export const fetchStockFilings = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<StockFilingsApiResponse>> => {
	return fetchApiDataFlatJson<StockFilingsApiResponse>(options);
};

export const fetchCompanyDetails = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<StockCompanyDetailsApiResponse>> => {
	return fetchApiDataFlatJson<StockCompanyDetailsApiResponse>(options);
};

export const fetchStockData = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<StockDataApiResponse>> => {
	return fetchApiDataFlatJson<StockDataApiResponse>(options);
};

export const fetchStockSummary = async (options: {
    config: AxiosRequestConfig;
}): Promise<ApiResponse<FinancialSummaryApiResponse>> => {
    return fetchApiDataFlatJson<FinancialSummaryApiResponse>(options);
};

export const fetchStockPriceChange = async (options: {
    config: AxiosRequestConfig;
}): Promise<ApiResponse<StockPriceChangeApiResponse>> => {
    return fetchApiDataFlatJson<StockPriceChangeApiResponse>(options);
};

export const fetchAiAnalysis = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<AiAnalysisApiResponse>> => {
	return fetchApiData<AiAnalysisApiResponse>(options);
};

export const fetchApiWakeupRequest = async (options: {
	config: AxiosRequestConfig;
}): Promise<any> => {
	return fetchApiData<any>(options);
};

export const postNewsLetterRequest = async (options: {
	config: AxiosRequestConfig;
}): Promise<ApiResponse<NewsletterControllerApiResponse>> => {
	return fetchApiDataFlatJson<NewsletterControllerApiResponse>(options);
};

export { fetchApiDataFlatJson };
